import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import SearchBox from '@com/_atoms/SearchInput';
import { MainLayout } from '@com/Layout';
import BasketItems from '@com/shared/basket-items';
import DoctorTab from '@containers/Diseases/components/doctorTab';
import DrugTab from '@containers/Diseases/components/drugTab';
import { debounce } from '@utilities/debounce';

const DISEASE_PAGE_ID_VALIDATION = /^[0-9]+$/;

const DiseaseContainer = () => {
  const { query, push, pathname, replace, isReady, back: goBack } = useRouter();
  //TODO: Instead of using regex, create a hook to have control on the ulr
  const hasId = DISEASE_PAGE_ID_VALIDATION.test(query.id as string);
  const currentId = query.id;

  const initialSearch = Array.isArray(query?.search)
    ? query?.search[0]
    : query?.search || '';

  const tab = query?.tab;

  const [searchText, setSearchText] = useState<string>(initialSearch);
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (!value || value.length > 2) {
        push(
          {
            pathname: pathname,
            query: {
              ...query,
              id: currentId,
              search: value,
            },
          },
          undefined,
          { shallow: true },
        );
      }
    }, 600),
    [pathname, push, query, currentId],
  );

  const handleChangeSearchInput = (value: string) => {
    setSearchText(value);
    if (currentId) debouncedSearch(value);
  };

  useEffect(() => {
    setSearchText('');
    const { search, ...rest } = query;
    if (isReady && !hasId) {
      void push('/404');
      return;
    }
    if (currentId) {
      void replace(
        {
          pathname,
          query: {
            ...rest,
            ...(currentId && { id: currentId }),
            tab: tab,
          },
        },
        undefined,
        { shallow: true },
      );
    }
  }, [tab, currentId, isReady]);

  return (
    <MainLayout
      hasHeader
      headerType="withoutLogo"
      hasBackButton
      hasBasketIcon
      title={query?.title}
      backIconHandler={() => goBack()}
      scrollToTop
    >
      <div className="px-4 sticky top-0 z-20 bg-white py-2 flex flex-col gap-y-2">
        <SearchBox
          value={searchText}
          onChange={handleChangeSearchInput}
          onEnter={(val) => debouncedSearch(val)}
          autoFocus={false}
          title={tab === 'doctor' ? 'جستجو در پزشک' : 'جستجو در دارو'}
        />
        <div className="mb-2">
          <div className="relative flex items-center w-full h-10 bg-gray-200 p-1 gap-x-4 rounded-full">
            <div className="absolute w-[calc(100%-8px)] h-8">
              <div
                className={classNames(
                  'absolute h-8 w-1/2 rounded-full bg-white transition-transform duration-200 left-0',
                  tab === 'doctor' ? 'translate-x-full' : 'translate-x-0',
                )}
              />
            </div>

            <div
              className="h-8 rounded-full w-full flex items-center justify-center cursor-pointer text-sm z-10"
              onClick={() => {
                push(
                  {
                    pathname,
                    query: {
                      ...query,
                      tab: 'doctor',
                      pageNumber: 1,
                      pageSize: 20,
                    },
                  },
                  undefined,
                  {
                    shallow: true,
                  },
                );
              }}
            >
              پیشنهاد پزشک
            </div>
            <div
              className="h-8 rounded-full w-full flex items-center justify-center cursor-pointer text-sm z-10"
              onClick={() => {
                push(
                  {
                    pathname,
                    query: {
                      ...query,
                      tab: 'drug',
                      pageNumber: 1,
                      pageSize: 20,
                    },
                  },
                  undefined,
                  {
                    shallow: true,
                  },
                );
              }}
            >
              پیشنهاد دارو
            </div>
          </div>
        </div>
      </div>

      {tab === 'drug' && <DrugTab />}
      {tab === 'doctor' && <DoctorTab />}
      <BasketItems />
    </MainLayout>
  );
};

export default DiseaseContainer;
